import { ISalesforceAddressDTO, ISalesforceLookupDTO, ISalesforceOrderRecordDTO } from '../../../../../api/salesforce-order.dao';
import { FormVmValueType } from '../../../../../infrastructure/form-viewmodel-controller';
import {
    DEFAULT_ACCOUNT_LEGAL_ENTITY,
    DEFAULT_HYBRID_EVENT,
    DEFAULT_LASSO_SYNC_STATUS,
    DEFAULT_TEAMS_CREATED,
} from './add-salesforce-order.fields';
import { salesforceHtmlToPlainText } from './rich-text-to-plain-text';

const lookupId = (lookup: ISalesforceLookupDTO | null | undefined): string => lookup?.id ?? '';

const lookupOrTextId = (value: ISalesforceLookupDTO | string | null | undefined): string => {
    if (value == null) {
        return '';
    }
    return typeof value === 'string' ? value : value.id;
};

const addressLines = (address: ISalesforceAddressDTO | null | undefined): string => {
    if (!address) {
        return '';
    }
    const cityLine = [address.city, address.state, address.postalCode].filter(Boolean).join(' ');
    return [address.street, cityLine, address.country].filter(part => !!part && part.trim() !== '').join('\n');
};

export const mapOrderToFormValues = (order: ISalesforceOrderRecordDTO): Record<string, FormVmValueType> => ({
    // Order details
    orderStatus: order.orderStatus ?? '',
    billingStatus: order.billingStatus ?? '',
    contractNumber: lookupOrTextId(order.contractNumber),
    multiEventOrder: order.multiEventOrder ?? '',
    parentOrder: lookupId(order.parentOrder),
    requesterContact: lookupId(order.requesterContact),
    corporateRelated: lookupId(order.corporateRelated),
    opportunity: lookupId(order.opportunityName),
    channelAgencyReferral: lookupId(order.channelAgencyReferral),
    hostLob: order.hostLob ?? '',
    hostSubLob: order.hostSubLob ?? '',
    feedbackFormUrl: order.feedbackFormUrl ?? '',
    orderType: order.orderType ?? '',
    orderSubtype: order.orderSubtype ?? '',
    eventType: order.eventType ?? '',
    eventTypeClient: order.eventTypeClient ?? '',
    cancellationFee: order.cancellationFee ?? '',
    cancellationReason: order.cancellationReason ?? '',
    cancellationOther: order.cancellationOther ?? '',
    syncToTeams: order.syncToTeams === false ? '' : 'Yes',
    noStaffRequired: order.noStaffRequired === true ? 'Yes' : '',
    hybridEvent: order.hybridEvent ?? DEFAULT_HYBRID_EVENT,
    highProfileOrder: order.highProfileOrder === true ? 'Yes' : '',
    highProfileReasoning: order.highProfileReasoning ?? '',
    highProfileAttendeeDetails: order.highProfileAttendeeDetails ?? '',
    accountLegalEntity: order.accountLegalEntity ?? DEFAULT_ACCOUNT_LEGAL_ENTITY,
    isThisEventRepeatable: order.isThisEventRepeatable ?? '',

    // Integration
    lassoSyncStatus: order.lassoSyncStatus ?? DEFAULT_LASSO_SYNC_STATUS,
    lassoSyncDatetime: order.lassoSyncDatetime ?? '',
    teamsCreated: order.teamsCreated ?? DEFAULT_TEAMS_CREATED,
    processedForArchive: order.processedForArchive === true ? 'Yes' : '',

    // Billing
    billToLegalEntity: lookupId(order.billToLegalEntity),
    billingAddress: addressLines(order.billingAddress),
    billingContact: lookupId(order.billingContact),
    billingContactEmail: order.billingContactEmail ?? '',
    additionalRecipient1: lookupId(order.additionalRecipient1),
    additionalRecipient1Email: order.additionalRecipient1Email ?? '',
    additionalRecipient2: lookupId(order.additionalRecipient2),
    additionalRecipient2Email: order.additionalRecipient2Email ?? '',
    billingMethod: order.billingMethod ?? '',
    clientCostCode: order.clientCostCode ?? '',
    secondaryPO: order.secondaryPO ?? '',
    billingTimeSensitivity: order.billingTimeSensitivity ?? '',
    billingNotes: order.billingNotes ?? '',

    // Post-event
    qaChatEnabled: order.qaChatEnabled ?? '',
    qaReportSent: order.qaReportSent ?? '',
    eventRanSmoothly: order.eventRanSmoothly === true ? 'Yes' : '',
    postEventNotes: salesforceHtmlToPlainText(order.postEventNotes ?? ''),

    // Products / description
    priceBook: order.pricebook?.name ?? '',
    currency: order.currencyIsoCode ?? '',
    description: salesforceHtmlToPlainText(order.description ?? ''),
});
